import React, {FC} from 'react';

//MUI
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

//MUI-ICONS
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';

import HalfScreenWrapper from '../HalfScreenWrapper';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';

import {useTranslation} from 'react-i18next';
import {lighten} from '@mui/system/colorManipulator';
import {secondaryColor} from '@/theme';
import {SxProps} from '@mui/system';

const styles: Record<string, SxProps> = {
  avatar: {
    m: 1,
    bgcolor: 'primary.main'
  },
  textField: {
    mb: 1,
    bgcolor: lighten(secondaryColor, 0.95)
  },
  errorText: {
    color: 'error.main',
    mb: 2,
    ml: 2
  },
  loginButton: {
    mt: 3,
    mb: 2,
    borderRadius: 0,
    height: '48px',
  },
  startAdornment: {
    color: 'primary.main',
    mr: 1,
    fontSize: '32px'
  }
};

export type LoginFormProps = {
  loginErrorMessage?: string,
  username: string,
  password: string,
  isLoading?: boolean,
  onUsernameChanged: (name: string) => void,
  onPasswordChanged: (password: string) => void,
  onLoginButtonClicked: () => void,
}

const LoginForm: FC<LoginFormProps> = ({
  loginErrorMessage,
  username,
  password,
  isLoading,
  onUsernameChanged,
  onPasswordChanged,
  onLoginButtonClicked
}) => {

  const {t} = useTranslation();

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => onUsernameChanged(e.target.value);
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => onPasswordChanged(e.target.value);
  const handleLoginClick = (e: React.FormEvent) => {
    e.preventDefault();
    onLoginButtonClicked();
  };

  return <HalfScreenWrapper>
    <Avatar sx={styles.avatar}>
      <LockIcon sx={{color: 'common.white'}}/>
    </Avatar>
    <Typography component="h1" variant="h5" sx={{mb: 6}}>{t('authentication.login')}</Typography>
    <Box component="form" onSubmit={handleLoginClick} noValidate>
      <TextField
        value={username}
        onChange={handleNameChange}
        placeholder={t('authentication.email')}
        required
        fullWidth
        slotProps={{input: { startAdornment: <AccountCircleIcon sx={styles.startAdornment} /> }}}
        sx={styles.textField}
      />
      <TextField
        value={password}
        type="password"
        onChange={handlePasswordChange}
        placeholder={t('authentication.password')}
        required
        fullWidth
        slotProps={{ input: {startAdornment: <LockIcon sx={styles.startAdornment}/>}}}
        sx={styles.textField}
      />
      <Typography variant="body2" sx={styles.errorText}>{loginErrorMessage ? t(loginErrorMessage) : ''}</Typography>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={styles.loginButton} disabled={!username || !password}
      >
        {isLoading ? `${t('authentication.loggingIn')}...` : t('authentication.login')}
      </Button>
    </Box>
  </HalfScreenWrapper>;
};

export default LoginForm;